// ==================== i18n SYSTEM ====================
// Internationalization system for KIXIK Equipment Management
// Uses direct JSON imports instead of fetch

// Import translations directly
import enTranslations from '../translations/en.json'
import etTranslations from '../translations/et.json'
import ruTranslations from '../translations/ru.json'
import fiTranslations from '../translations/fi.json'
import deTranslations from '../translations/de.json'
import lvTranslations from '../translations/lv.json'
import ltTranslations from '../translations/lt.json'

// Initialize translations
const translations = {
  en: enTranslations,
  et: etTranslations,
  ru: ruTranslations,
  fi: fiTranslations,
  de: deTranslations,
  lv: lvTranslations,
  lt: ltTranslations
}

let currentLanguage = localStorage.getItem('kixik_language') || 'en'

/**
 * Get nested value from object using dot notation
 * @param {object} obj - The object to search
 * @param {string} path - Dot-separated path (e.g., 'auth.login')
 * @returns {any} The value at the path
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Translation helper function
 * @param {string} key - Translation key (e.g., 'auth.login')
 * @param {object} params - Optional parameters for string interpolation
 * @returns {string} Translated text
 */
function t(key, params = {}) {
  const langData = translations[currentLanguage]
  if (!langData) {
    console.warn(`⚠️ Language data not found for: ${currentLanguage}`)
    return key
  }
  
  // Get translation using dot notation
  let translation = getNestedValue(langData, key)
  
  if (!translation) {
    console.warn(`⚠️ Translation missing for key: ${key} (lang: ${currentLanguage})`)
    
    // Try English as fallback
    if (currentLanguage !== 'en' && translations['en']) {
      translation = getNestedValue(translations['en'], key)
      if (translation) {
        console.log(`→ Using English fallback for: ${key}`)
      }
    }
    
    // If still not found, return the key
    if (!translation) {
      return key
    }
  }
  
  // Handle string interpolation (e.g., "Found {count} items" with params {count: 5})
  if (typeof translation === 'string' && Object.keys(params).length > 0) {
    return translation.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match
    })
  }
  
  return translation
}

/**
 * Change language and update UI
 * @param {string} lang - Language code ('en', 'et', 'ru')
 */
function setLanguage(lang) {
  if (!translations[lang]) {
    console.error(`❌ Language not supported: ${lang}`)
    return
  }
  
  currentLanguage = lang
  localStorage.setItem('kixik_language', lang)
  
  console.log(`🌍 Language changed to: ${lang}`)
  
  // Update all elements with translation attributes
  updateTranslations()
  
  // Update HTML lang attribute
  document.documentElement.lang = lang
  
  // Trigger custom event for other scripts to update
  window.dispatchEvent(new CustomEvent('languageChanged', { 
    detail: { language: lang } 
  }))
}

/**
 * Update all translatable elements on the page
 */
function updateTranslations() {
  // Update elements with data-i18n attribute (text content)
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n')
    const params = element.getAttribute('data-i18n-params')
    const parsedParams = params ? JSON.parse(params) : {}
    element.textContent = t(key, parsedParams)
  })
  
  // Update placeholders
  document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
    const key = element.getAttribute('data-i18n-placeholder')
    element.placeholder = t(key)
  })
  
  // Update titles
  document.querySelectorAll('[data-i18n-title]').forEach(element => {
    const key = element.getAttribute('data-i18n-title')
    element.title = t(key)
  })
  
  // Update values (for buttons, etc.)
  document.querySelectorAll('[data-i18n-value]').forEach(element => {
    const key = element.getAttribute('data-i18n-value')
    element.value = t(key)
  })
  
  // Update aria-label for accessibility
  document.querySelectorAll('[data-i18n-aria]').forEach(element => {
    const key = element.getAttribute('data-i18n-aria')
    element.setAttribute('aria-label', t(key))
  })
  
  // Update active language button
  document.querySelectorAll('.lang-btn').forEach(btn => {
    btn.classList.remove('active')
    if (btn.dataset.lang === currentLanguage) {
      btn.classList.add('active')
    }
  })
  
  console.log('✅ UI translations updated')
}

/**
 * Initialize i18n system (for compatibility, but not async anymore)
 */
function initI18n() {
  console.log('✅ i18n system initialized with direct imports')
  console.log(`Current language: ${currentLanguage}`)
  return Promise.resolve()
}

/**
 * Get current language code
 * @returns {string} Current language code
 */
function getCurrentLanguage() {
  return currentLanguage
}

/**
 * Check if translations are loaded
 * @returns {boolean} True if loaded
 */
function isI18nLoaded() {
  return true  // Always loaded with direct imports
}

// Export for use in other modules
export { 
  t, 
  setLanguage, 
  updateTranslations, 
  initI18n,
  getCurrentLanguage,
  isI18nLoaded
}

// Initialize on module load
console.log('🌍 i18n module loaded')
